import { useStorage } from "@platform/hooks";
import {
  type Dispatch,
  JSX,
  type SetStateAction,
  useEffect,
  useMemo,
} from "react";
import { cellClassName, DataTableV2 } from "@platform/design-system";
import { useSignedInUser } from "@platform/user/client";
import { Box } from "@mui/material";

import { useClientsTable } from "../hooks/useClientsTable";
import { ClientPageState } from "../pages/Clients/types";
import { headers, headersAdmin } from "../pages/Clients/utils";

import type { ColDef } from "ag-grid-community";

interface Props {
  brokerId?: number;
  search?: string;
  setIsError: Dispatch<SetStateAction<boolean>>;
  setTotal: Dispatch<SetStateAction<number>>;
}

function ClientsTable({
  brokerId,
  search,
  setIsError,
  setTotal,
}: Props): JSX.Element {
  const { getStorageItem, setStorageItem } = useStorage("session");
  const sessionStorageKey = brokerId
    ? `brokerClientPageState-${brokerId}`
    : "clientPageState";

  const clientPageState: ClientPageState =
    getStorageItem(sessionStorageKey) || {};

  const { isAdminOrConsultant } = useSignedInUser();

  const columns: ColDef[] = useMemo(
    () => (isAdminOrConsultant ? headersAdmin : headers),
    [isAdminOrConsultant],
  );

  const {
    onPaginationChanged,
    total,
    isError,
    onFirstDataRendered,
    initialState,
    serverSideDatasource,
    paginationPageSizeSelector,
    paginationPageSize,
    suppressServerSideFullWidthLoadingRow,
    tooltipShowDelay,
    autoSizeStrategy,
    defaultColDef,
    onGridReady,
    onGridPreDestroyed,
    onSortChanged,
  } = useClientsTable({
    brokerId,
    search: search?.trim().toLowerCase(),
    clientPageState,
    sessionStorageKey,
    setStorageItem,
    getStorageItem,
  });

  useEffect(() => setIsError(isError), [isError, setIsError]);
  useEffect(() => setTotal(total), [setTotal, total]);

  const gridOptions = useMemo(
    () => ({
      defaultColDef,
      onGridReady,
      onSortChanged,
    }),
    [defaultColDef, onGridReady, onSortChanged],
  );

  return (
    <Box
      data-testid="clients-table"
      sx={{
        [`& .${cellClassName} .ag-cell-wrapper`]: {
          display: "grid",
          alignItems: "center",
          minHeight: "100%",
        },
      }}
    >
      <DataTableV2
        columnDefs={columns}
        overlayNoRowsTemplate="No clients found"
        rowModelType="serverSide"
        serverSideDatasource={serverSideDatasource}
        onPaginationChanged={onPaginationChanged}
        onFirstDataRendered={onFirstDataRendered}
        onGridReady={onGridReady}
        onGridPreDestroyed={onGridPreDestroyed}
        onSortChanged={onSortChanged}
        initialState={initialState}
        paginationPageSizeSelector={paginationPageSizeSelector}
        paginationPageSize={paginationPageSize}
        tooltipShowDelay={tooltipShowDelay}
        suppressServerSideFullWidthLoadingRow={
          suppressServerSideFullWidthLoadingRow
        }
        autoSizeStrategy={autoSizeStrategy}
        gridOptions={gridOptions}
      />
    </Box>
  );
}

export default ClientsTable;
